/* eslint-disable react-hooks/set-state-in-effect -- this input intentionally mirrors external value changes into local edit state. */
import styled from "styled-components";
import {useEffect, useRef, useState} from "react";
import {StringUtils} from "../../../../utils/data/string/StringUtils";
import {ReactUtils} from "../../../utils/ReactUtils";
import {SelectInputV5} from "../../input/select/SelectInputV5";
import type {IInputPropsV5} from "../../../widgets/input/clicktoedit/InputUtils";
import {colorPalette} from "../../styles/colorPalette";
import {baseDistance, FlexCenterStyle, fontSize} from "../../styles/styles";
import {useClickOutside, useCloseOnKeyboardPress} from "../../utils";
import {KeyboardListener} from "../../../../utils/interaction/key/KeyboardListener";
import {NumberInputV5} from "./numeric/NumberInputV5";

const countryCodes: string[] = [
	"+1",
	"+7",
	"+20",
	"+27",
	"+30",
	"+31",
	"+32",
	"+33",
	"+34",
	"+36",
	"+39",
	"+40",
	"+41",
	"+43",
	"+44",
	"+45",
	"+46",
	"+47",
	"+48",
	"+49",
	"+51",
	"+52",
	"+53",
	"+54",
	"+55",
	"+56",
	"+57",
	"+58",
	"+60",
	"+61",
	"+62",
	"+63",
	"+64",
	"+65",
	"+66",
	"+81",
	"+82",
	"+84",
	"+86",
	"+90",
	"+91",
	"+92",
	"+93",
	"+94",
	"+95",
	"+98",
	"+211",
	"+212",
	"+213",
	"+216",
	"+218",
	"+220",
	"+221",
	"+222",
	"+223",
	"+224",
	"+225",
	"+226",
	"+227",
	"+228",
	"+229",
	"+230",
	"+231",
	"+232",
	"+233",
	"+234",
	"+235",
	"+236",
	"+237",
	"+238",
	"+239",
	"+240",
	"+241",
	"+242",
	"+243",
	"+244",
	"+245",
	"+246",
	"+248",
	"+249",
	"+250",
	"+251",
	"+252",
	"+253",
	"+254",
	"+255",
	"+256",
	"+257",
	"+258",
	"+260",
	"+261",
	"+262",
	"+263",
	"+264",
	"+265",
	"+266",
	"+267",
	"+268",
	"+269",
	"+290",
	"+291",
	"+297",
	"+298",
	"+299",
	"+350",
	"+351",
	"+352",
	"+353",
	"+354",
	"+355",
	"+356",
	"+357",
	"+358",
	"+359",
	"+370",
	"+371",
	"+372",
	"+373",
	"+374",
	"+375",
	"+376",
	"+377",
	"+378",
	"+379",
	"+380",
	"+381",
	"+382",
	"+383",
	"+385",
	"+386",
	"+387",
	"+389",
	"+420",
	"+421",
	"+423",
	"+500",
	"+501",
	"+502",
	"+503",
	"+504",
	"+505",
	"+506",
	"+507",
	"+508",
	"+509",
	"+590",
	"+591",
	"+592",
	"+593",
	"+595",
	"+597",
	"+598",
	"+599",
	"+670",
	"+672",
	"+673",
	"+674",
	"+675",
	"+676",
	"+677",
	"+678",
	"+679",
	"+680",
	"+681",
	"+682",
	"+683",
	"+685",
	"+686",
	"+687",
	"+688",
	"+689",
	"+690",
	"+691",
	"+692",
	"+850",
	"+852",
	"+853",
	"+855",
	"+856",
	"+880",
	"+886",
	"+960",
	"+961",
	"+962",
	"+963",
	"+964",
	"+965",
	"+966",
	"+967",
	"+968",
	"+970",
	"+971",
	"+972",
	"+973",
	"+974",
	"+975",
	"+976",
	"+977",
	"+992",
	"+993",
	"+994",
	"+995",
	"+996",
	"+998",
	"+1-242",
	"+1-246",
	"+1-264",
	"+1-268",
	"+1-284",
	"+1-340",
	"+1-345",
	"+1-441",
	"+1-473",
	"+1-649",
	"+1-664",
	"+1-670",
	"+1-671",
	"+1-684",
	"+1-721",
	"+1-758",
	"+1-767",
	"+1-784",
	"+1-787",
	"+1-809",
	"+1-868",
	"+1-869",
	"+1-876",
	"+44-1481",
	"+44-1534",
	"+44-1624",
	"+1-939",
	"+1-829",
	"+1-849",
];

const findPhoneParts = (value: string) => {
	const trimmedValue = StringUtils.trim(value);

	const result = {
		countryCode: countryCodes[0],
		phone: trimmedValue,
	};

	// we go from the top so "+1-767" is found before "+1"
	for (let i = countryCodes.length - 1; i >= 0; --i) {
		const country = countryCodes[i];

		if (trimmedValue.startsWith(country)) {
			result.countryCode = country;
			result.phone = trimmedValue.substring(country.length);
			break;
		}
	}
	return result;
};

export const PhoneInputV5 = (props: IInputPropsV5) => {
	const {disabled, onBlur, value, onChange} = props;

	const _element = useRef<HTMLDivElement>(null);
	const latestStateRef = useRef({
		isActive: false,
		wholePhoneValue: value,
		onBlur,
		onChange,
	});

	const [wholePhoneValue, setWholePhoneValue] = useState<string>(value);
	const [isActive, setIsActive] = useState(false);
	const [selectInputFocused, setSelectInputFocused] = useState(false);
	latestStateRef.current = {
		isActive,
		wholePhoneValue,
		onBlur,
		onChange,
	};

	const onClickElement = () => {
		setIsActive(true);
	};

	const onBlurElement = () => {
		if (isActive && !selectInputFocused) {
			setIsActive(false);
			onChange(wholePhoneValue);
			onBlur?.();
		}

		if (selectInputFocused) {
			setSelectInputFocused(false);
		}
	};

	useClickOutside([_element], onBlurElement, "mouseup");
	useCloseOnKeyboardPress(onBlurElement, KeyboardListener.KEY_ENTER);

	useEffect(() => {
		setWholePhoneValue(value);
	}, [value]);

	useEffect(() => {
		return () => {
			const latestState = latestStateRef.current;

			if (latestState.isActive) {
				latestState.onChange(latestState.wholePhoneValue);
				latestState.onBlur?.();
			}
		};
	}, []);

	return !isActive ? (
		<PhoneLabelStyled onClick={onClickElement}>
			{value && (
				<>
					<div className="countryCode">{findPhoneParts(wholePhoneValue).countryCode}</div>
					<div className="phone">{findPhoneParts(wholePhoneValue).phone}</div>
				</>
			)}
		</PhoneLabelStyled>
	) : (
		<PhoneInputStyled
			ref={_element}
			className={ReactUtils.cls({disabled})}
		>
			<SelectInputV5
				selected={findPhoneParts(wholePhoneValue).countryCode}
				options={countryCodes}
				onChange={(value) => {
					setWholePhoneValue(StringUtils.trim(value) + StringUtils.trim(findPhoneParts(wholePhoneValue).phone));
				}}
				focused={selectInputFocused}
				onClick={(e) => {
					e.stopPropagation();
					setSelectInputFocused(!selectInputFocused);
				}}
			/>
			<NumberInputV5
				value={StringUtils.trim(findPhoneParts(wholePhoneValue).phone) as any}
				onChange={(value) => {
					if (value === null) {
						setWholePhoneValue(StringUtils.trim(findPhoneParts(wholePhoneValue).countryCode));
					}
					if (value?.toString() !== findPhoneParts(wholePhoneValue).phone) {
						setWholePhoneValue(StringUtils.trim(findPhoneParts(wholePhoneValue).countryCode) + StringUtils.trim(value?.toString()));
					}
				}}
				onClick={() => setSelectInputFocused(false)}
				autoFocus={true}
				onlyNaturalNumbers={true}
			/>
		</PhoneInputStyled>
	);
};

export const PhoneInputStyled = styled.div`
	display: flex;
	align-items: center;
	position: relative;
`;

export const PhoneLabelStyled = styled.div`
	font-size: ${fontSize.md};
	line-height: 16px;
	${FlexCenterStyle};
	padding: 0 ${baseDistance.sm};

	.countryCode {
		color: ${colorPalette.gray.c700Dark};
	}
`;
